import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CategoryPayload, CourseCategory } from '../../types/learning.types';
import { learningApi } from '../../utils/learning-api';

/** The five pill colours offered by the prototype's category dialog. */
const SWATCHES = ['#dc2626', '#d97706', '#2563eb', '#059669', '#7c3aed'];

interface CategoryDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Null creates; a category edits it. */
    category: CourseCategory | null;
    onSaved: (category: CourseCategory) => void;
}

export const CategoryDialog: React.FC<CategoryDialogProps> = ({
    open,
    onOpenChange,
    category,
    onSaved,
}) => {
    const [name, setName] = useState('');
    const [colour, setColour] = useState(SWATCHES[0]);
    const [saving, setSaving] = useState(false);

    // Re-seed the form each time the dialog opens (or switches target), so a cancelled
    // edit never leaks its values into the next open.
    useEffect(() => {
        if (!open) return;
        setName(category?.name ?? '');
        setColour(category?.colour ?? SWATCHES[0]);
    }, [open, category]);

    const handleSave = async () => {
        const trimmed = name.trim();
        if (!trimmed) {
            toast.error('A category name is required');
            return;
        }

        const payload: CategoryPayload = { name: trimmed, colour };
        setSaving(true);
        try {
            const saved = category
                ? await learningApi.categories.update(category.id, payload)
                : await learningApi.categories.create(payload);
            toast.success(category ? 'Category updated' : 'Category created');
            onSaved(saved);
            onOpenChange(false);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to save category');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{category ? 'Edit category' : 'New category'}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="category-name">Name</Label>
                        <Input
                            id="category-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Plant & Equipment"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label>Pill colour</Label>
                        <div className="flex gap-2.5">
                            {SWATCHES.map((swatch) => (
                                <button
                                    key={swatch}
                                    type="button"
                                    aria-label={`Colour ${swatch}`}
                                    aria-pressed={colour === swatch}
                                    onClick={() => setColour(swatch)}
                                    className={`h-8 w-8 rounded-lg border-2 ${
                                        colour === swatch ? 'border-foreground' : 'border-transparent'
                                    }`}
                                    style={{ backgroundColor: swatch }}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button onClick={handleSave} disabled={saving}>
                        {saving && <Loader2 size={16} className="animate-spin" />}
                        Save category
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default CategoryDialog;
